// Story 7.3 (ADO-1139) — availability state in a Zustand store (AC1/AC2).
//
// project-context React rules (ADR-004): Zustand for all client-side global
// state. State is keyed by `skuCode` → { band, status, quantityOnHand }:
// populated by the batch fetch when a listing renders, patched in place by
// Socket.io pushes. Cards subscribe per-SKU; a SKU with no entry renders no
// badge (unknown is never claimed as In Stock).
//
// Write-ordering guard (the 7.2 review lesson — stale settlement must not
// clobber newer state): every write carries a monotonically increasing stamp.
// A socket push always wins (it is the newest real-time signal); a batch-fetch
// result applies per-SKU only when nothing newer (a push, or a later fetch)
// has written that SKU since the fetch STARTED. Failed fetches keep the last
// good entries on screen (retain-through-error posture, browse/search parity).

import { create } from 'zustand';
import {
  fetchAvailability,
  type AvailabilityBand,
  type SupplierAvailabilityStatus,
} from '../api/availability.client';

export interface AvailabilityEntry {
  readonly band: AvailabilityBand;
  readonly status: SupplierAvailabilityStatus;
  readonly quantityOnHand: number;
}

/** The push payload shape the gateway emits (lock-step with AvailabilityPushPayload). */
export interface AvailabilityPush {
  readonly depotId: string;
  readonly entries: ReadonlyArray<{
    readonly skuCode: string;
    readonly band: AvailabilityBand;
    readonly status: SupplierAvailabilityStatus;
    readonly quantityOnHand: number;
  }>;
  /**
   * SKUs that now have no stock data at the depot (the feed retired their level). Optional: a
   * server from before Enabler E7-B (ADO-2273) does not send it.
   */
  readonly noStockDataSkuCodes?: ReadonlyArray<string>;
}

/**
 * What one caller's read did — Story 7-FU-K (ADO-1528), AC6. THREE outcomes, not two: a depot
 * switch that pre-empts an in-flight read is not a missing guard, it is a missing VALUE. The
 * predecessor was a boolean, so "we deliberately did not spend the request" and "the endpoint
 * could not answer" both arrived as `false`, and the read cycle charged a question that never
 * failed — three quick depot toggles parked the card on the terminal error for the whole
 * cooldown.
 *
 * - `'answered'`   — every SKU THIS caller asked for got an answer. Note that "answered"
 *                    includes "the server has no stock row for it at this depot": a successful
 *                    read legitimately omits such a SKU (see `checkedSkus`).
 * - `'unread'`     — a request WAS put on the wire and at least one of this caller's SKUs came
 *                    back unestablished. An ENDPOINT failure: charged against the retry budget
 *                    and laddered, whether or not the caller is still there to see it.
 * - `'superseded'` — the depot moved, so this caller's question is no longer the live one:
 *                    either no request was spent at all (the switch landed between enqueue and
 *                    flush) or its result belongs to a depot we have left. NEVER laddered,
 *                    never surfaced as an error, and never charged — nothing failed.
 *
 * `'unread'` outranks `'superseded'` for a caller whose SKUs span both: an endpoint that
 * demonstrably could not answer is the fact worth acting on.
 */
export type ReadOutcome = 'answered' | 'unread' | 'superseded';

/** What one flushed batch did, per SKU. A batch is SHARED — merged across the tick and chunked
 *  under that — so each caller intersects these with its OWN SKUs (see `PendingBatch.settle`). */
interface BatchOutcome {
  /** SKUs a request was spent on and which it could not establish. */
  readonly unread: ReadonlySet<string>;
  /** SKUs whose read was superseded by a depot switch — see `ReadOutcome`. */
  readonly superseded: ReadonlySet<string>;
}

/** Per-call tracking posture for `loadForSkus` (see `LoadForSkusOptions.track`). */
export interface LoadForSkusOptions {
  /**
   * Add the requested SKUs to the SESSION-tracked set (default `true`) — what a LISTING
   * wants: it renders those SKUs for as long as the session lasts, so they stay pushed-to
   * and refetched on reconnect. Pass `false` for a read whose interest is TRANSIENT (the
   * per-card substitute-candidate read, 7-FU-I): those SKUs are claimed with
   * `retainSkus`/`releaseSkus` instead, so the tracked set can SHRINK again when the card
   * unmounts rather than growing ~10 candidates per out-of-stock card for the session —
   * unbounded growth makes every reconnect catch-up progressively more expensive.
   */
  readonly track?: boolean;
}

interface AvailabilityStoreState {
  /** The session depot the entries belong to; set on the first load. */
  depotId: string | null;
  entries: Readonly<Record<string, AvailabilityEntry>>;
  /**
   * Every SKU we hold a settled ANSWER for at the current depot — a returned stock row,
   * a read that established the SKU has NO row here, or a push. This is what tells
   * "checked, not stocked at this depot" from "not checked yet", which `entries` alone
   * cannot express: BOTH are an absent entry.
   *
   * That distinction is load-bearing in two places, and conflating it produced findings
   * in five separate rounds of this story:
   *  - the substitute-candidate read derives "unestablished" from THIS, not from entry
   *    presence, so a successful read that legitimately returned no row for a candidate
   *    settles the question instead of reading as an unresolved unknown;
   *  - `ProductCard` bridges the availability gap a depot switch opens by holding its
   *    out-of-stock trigger while its own SKU is unknown — which needs a settle
   *    condition, or a product with no row at the new depot holds it for the session.
   *
   * A read that FAILED for a SKU deliberately leaves it out (see `fetchAvailability`'s
   * `unreadSkus`), so an outage never presents as an authoritative empty. Depot-scoped:
   * cleared on a switch and on `reset()`, and dropped with the entry when `releaseSkus`
   * evicts an untracked SKU.
   */
  checkedSkus: ReadonlySet<string>;
  /**
   * Every SKU currently of interest — the union of the SESSION-tracked set (SKUs a
   * listing asked for) and the REFCOUNTED claims held by mounted cards
   * (`retainSkus`). The reconnect catch-up refetches exactly this set (cheap
   * consistency after a socket gap) and `applyPush` filters against it.
   */
  trackedSkus: ReadonlySet<string>;
  /**
   * Batch-load availability for a listing's SKUs at the session depot. Resolves once the read
   * has SETTLED, to the `ReadOutcome` for the SKUs THIS CALLER asked for (the last good entries
   * are retained either way). The outcome matters because a SUCCESSFUL read legitimately omits
   * a SKU with no StockLevel row at the depot — so "no entry" alone can't tell "checked, not
   * stocked here" from "couldn't check" — and because a read pre-empted by a depot switch is
   * neither. The substitute OOS-gate (7-FU-I) consumes this: it opens the panel from the LIVE
   * entries (so it self-heals on a later push/refetch) but only shows the error label on an
   * `'unread'`, not when a successful read simply returned no stock and not when the question
   * was superseded.
   *
   * The signal is PER-CALLER, not per-batch: reads are merged across the tick and
   * chunked under it, so another caller's chunk failing must not report THIS caller's
   * SKUs as unreadable. Each caller is told about its own SKUs only.
   *
   * Requests are collapsed twice over, so a per-card read can't fan out:
   * - SAME-TICK BATCHING. Every call made in one tick is merged into ONE request,
   *   flushed on a microtask. A grid of M out-of-stock cards mounts M substitute-
   *   candidate reads in a single commit; they share one request, well inside
   *   `MAX_AVAILABILITY_BATCH_SIZE` (the client chunks anything larger).
   * - IN-FLIGHT COALESCING. A later-tick caller for a SKU whose read is ALREADY on the
   *   wire at this depot rides that request instead of issuing a duplicate, and resolves
   *   (with its success signal) when it settles.
   */
  loadForSkus: (
    depotId: string,
    skuCodes: readonly string[],
    options?: LoadForSkusOptions,
  ) => Promise<ReadOutcome>;
  /**
   * Claim `skuCodes` as tracked for as long as the caller is mounted (REFCOUNTED, so
   * overlapping claims from several cards are independent). Used by the per-card
   * substitute-candidate read, whose interest ends with the card — unlike a listing's
   * session-long interest. Always pair with `releaseSkus` in the effect cleanup.
   */
  retainSkus: (skuCodes: readonly string[]) => void;
  /**
   * Release a `retainSkus` claim. A SKU with no claims left AND no session interest drops
   * out of `trackedSkus` (no more pushes / catch-up reads) — and its entry is EVICTED with
   * it, because an entry nothing tracks is an entry nothing can keep current: no push
   * reaches it (`applyPush` filters on `trackedSkus`) and no reconnect catch-up re-reads it,
   * so stock can move arbitrarily far while it sits in the cache. Keeping it let a card
   * remount, find the entry "already established", skip its read entirely and offer a
   * substitute that had gone out of stock in the gap (PR #160 round 11, F2).
   *
   * The invariant is `entries ⊆ interest`, enforced here on release and in `flushBatch` on
   * write — and, because interest can LAPSE AND RETURN while a read is on the wire, a write
   * must belong to the interest that is current and not merely to some interest (the claim
   * generation this bumps; see `claimGenerations`). Each has its own guard in
   * `useAvailabilityStore.spec.ts` (eviction, retention while any interest holds, no
   * resurrection, no write from a superseded claim). No badge on screen
   * blanks as a result: every component that renders an entry declares its own session
   * interest via `useAvailability` (CategoryListing, ProductSearch, SearchTypeahead,
   * ProductDetail, PdpRecommendations, UpsellModal — verified by grep), and session interest
   * alone keeps a SKU tracked. Only per-card CANDIDATE claims — the substitute SKUs nothing
   * else renders — are evictable.
   */
  releaseSkus: (skuCodes: readonly string[]) => void;
  /** Apply a real-time push (socket layer calls this). */
  applyPush: (push: AvailabilityPush) => void;
  /** Re-fetch every tracked SKU (reconnect catch-up). No-op when nothing tracked. */
  refetchTracked: () => Promise<void>;
  /** Test/session hygiene — drop all state. */
  reset: () => void;
}

/** The band/status vocabularies this build can render (rolling-deploy guard). */
const KNOWN_BANDS: ReadonlySet<string> = new Set(['IN_STOCK', 'LOW_STOCK', 'OUT_OF_STOCK']);
const KNOWN_STATUSES: ReadonlySet<string> = new Set(['AVAILABLE', 'UNAVAILABLE']);

let writeStampCounter = 0;
/**
 * Per-SKU stamp of the write that produced the current ANSWER for that SKU — a returned
 * row, an omission tombstone, or a push. Depot-scoped (cleared on a switch), and dropped
 * with the entry when `releaseSkus` evicts an untracked SKU.
 *
 * Its KEY SET is exactly `checkedSkus`, which is why that is projected from here rather
 * than maintained alongside: two structures with the same membership rule drift, and this
 * one already had to be exactly right for write-ordering.
 */
let writeStamps: Record<string, number> = {};

/** `checkedSkus` as the store exposes it — see the field's contract. Projected from
 *  `writeStamps` on every write so the two cannot disagree. */
function checkedProjection(): ReadonlySet<string> {
  return new Set(Object.keys(writeStamps));
}

// In-flight read coalescing. Keyed by `${depotId} ${sku}` → the promise of the read
// currently fetching that SKU (resolving to that batch's per-SKU outcome). A second caller for
// a SKU already in flight at the same depot RIDES that promise instead of issuing a
// duplicate request. Distinct from the candidate read's established-entry skip: that avoids
// re-reading stock we already hold (which refetchTracked deliberately does NOT skip), this
// avoids duplicate CONCURRENT reads. Entries are cleared when their read settles, and on a
// depot switch. The reconnect catch-up (refetchTracked) also drops its tracked SKUs' entries
// here before reading, so it can never ride a pre-gap read that would refresh nothing.
let inFlightReads: Record<string, Promise<BatchOutcome>> = {};
const inFlightKey = (depotId: string, sku: string): string => `${depotId} ${sku}`;

// SAME-TICK request batching. In-flight coalescing only helps a caller whose SKU is ALREADY
// on the wire; M cards mounting in ONE commit each ask for a DISTINCT candidate set, so
// each would issue its own GET. SKUs asked for in the same tick are accumulated here and
// flushed as ONE request on a microtask — every caller in the tick rides the same promise.
// Only one batch is ever open, and only for the current depot: a depot switch drops it (its
// results would belong to the old depot), and the queued flush then finds itself superseded.
interface PendingBatch {
  readonly depotId: string;
  readonly skus: Set<string>;
  /** Resolves to this batch's per-SKU outcome — see `settle`. */
  readonly promise: Promise<BatchOutcome>;
  /**
   * Settle the batch with its per-SKU outcome. SETS rather than one success value, because a
   * batch is shared: it carries every read asked for in the tick, and it is chunked under
   * that. One chunk failing must leave the other chunks' callers reporting success, so each
   * caller intersects these with its OWN SKUs.
   */
  readonly settle: (outcome: BatchOutcome) => void;
  /** The write stamp for this batch's results, taken when the batch OPENED — i.e. when a
   *  caller asked, not a microtask later when it flushes. The guard's contract is "apply
   *  only if nothing newer has written this SKU since the read started"; a push that lands
   *  between the ask and the flush must still win, so the stamp cannot be deferred. */
  readonly startStamp: number;
  /** Each SKU's CLAIM GENERATION as it stood when this batch asked about it — see
   *  `claimGenerations`. Per-SKU rather than one stamp for the batch, because a batch is
   *  shared: one caller releasing its candidates must not disqualify another caller's SKUs
   *  in the same request. */
  readonly claimGens: Map<string, number>;
}
let pendingBatch: PendingBatch | null = null;

// Tracking is REFCOUNTED so the tracked set can shrink again. `sessionSkus` is a listing's
// session-long interest: sticky, cleared only on a depot switch or reset() — the 7.3
// semantic. `retainedRefs` counts the live claims mounted cards hold on transient SKUs (a
// card's substitute candidates, up to 10 each): a claim is dropped on unmount, so browsing
// doesn't grow the set — and with it every reconnect catch-up and every push filter — an
// order of magnitude faster than the listing itself. Retains are NOT cleared on a depot
// switch: they represent which cards are on screen, which the switch doesn't change (the
// depot-scoped state is the entries).
let sessionSkus = new Set<string>();
let retainedRefs: Record<string, number> = {};

/**
 * Per-SKU CLAIM GENERATION — bumped every time a SKU's entry is EVICTED because its last
 * claim was released. A read records the generation of each SKU it asks about, and
 * `flushBatch` writes a SKU only if that generation still stands.
 *
 * It closes the gap the eviction itself opens. `releaseSkus` deletes the entry, its write
 * stamp and its in-flight marker precisely so a later claim re-reads rather than trusting
 * stock nothing was watching. But a claim can return BEFORE the released read settles, and
 * then both of the write-side guards pass: interest is renewed (`trackedUnion` has the SKU
 * again) and the deleted stamp compares as `0 > startStamp` = false. So the pre-gap response
 * wrote its stale row back and marked it `checked` — and a card mounting in that window found
 * the SKU already answered and skipped its read entirely, which is the exact hole eviction
 * was introduced to close (round 15, F2).
 *
 * Distinct from the interest check, which it does not replace: interest covers "released and
 * NOT reclaimed", this covers "released and reclaimed since". Both are on the write path.
 *
 * GROWTH, stated rather than claimed away (the round-14 lesson about the retry register): this
 * is not pruned, so it holds one small integer per SKU that has held and lost a per-card claim
 * in this session — the order of the SKUs the user has scrolled past, NOT the combinatorial
 * `skus|depot` shape that needed a size cap. It is cleared on `reset()` and deliberately
 * survives a depot switch, since claims track which cards are mounted and a switch does not
 * change that. Pruning it correctly would need a refcount over the batches that have recorded
 * each SKU: deleting an entry resets the comparison to 0, which is stricter for a batch that
 * recorded a bumped generation but would WAIVE the guard for one that recorded 0 — so a
 * cheaper prune would silently reopen exactly the hole this closes.
 */
let claimGenerations: Record<string, number> = {};

/** The public tracked set: session interest ∪ live per-card claims. */
function trackedUnion(): ReadonlySet<string> {
  const union = new Set(sessionSkus);
  for (const sku of Object.keys(retainedRefs)) {
    if ((retainedRefs[sku] ?? 0) > 0) union.add(sku);
  }
  return union;
}

export const useAvailabilityStore = create<AvailabilityStoreState>((set, get) => {
  /**
   * Send one accumulated batch. Runs on a microtask after the tick that opened it, so it
   * carries every SKU asked for in that tick. Always settles its promise (all the tick's
   * callers ride it) and always clears its in-flight entries.
   */
  async function flushBatch(batch: PendingBatch): Promise<void> {
    if (pendingBatch === batch) pendingBatch = null; // closed: later calls open a new one
    const { depotId, startStamp } = batch;
    const toFetch = [...batch.skus];
    // This batch's per-SKU outcome. Starts as "a request was spent and answered none of them"
    // and is narrowed by whichever leg below runs, so every early exit reports honestly.
    let unread: ReadonlySet<string> = new Set(toFetch);
    let superseded: ReadonlySet<string> = new Set<string>();
    try {
      if (get().depotId !== depotId) {
        // The depot moved between enqueue and flush: these results would belong to the old
        // depot and be discarded on arrival, so don't spend the request. That makes every SKU
        // SUPERSEDED, not unread — ADO-1528 Item 2. Reporting them unread told the read cycle
        // the endpoint had failed, so a question nobody had asked yet was charged and, after
        // three quick toggles, parked on the terminal error for the whole cooldown. The
        // callers ARE keyed by depot and have moved on, but the cycle they left behind is
        // still holding a budget, and it must get that budget back.
        superseded = new Set(toFetch);
        unread = new Set<string>();
      } else {
        const { states, unreadSkus } = await fetchAvailability(depotId, toFetch);
        // The depot may have SWITCHED while this fetch was in flight — its results
        // belong to the old depot and must never patch the new depot's entries. The
        // stamps alone can't catch this: the switch resets writeStamps, so a stale
        // settlement would pass the per-SKU comparison (7.3 review finding).
        if (get().depotId === depotId) {
          unread = new Set(unreadSkus);
          const next: Record<string, AvailabilityEntry> = { ...get().entries };
          const returned = new Set(states.map((s) => s.skuCode));
          // Interest as it stands NOW, not when the read was issued: a card can release its
          // last candidate claim while this request is on the wire, and writing the result
          // back would resurrect exactly the untracked entry `releaseSkus` evicted — the
          // next claim would then skip its read again (round 11, F2). Upholds
          // `entries ⊆ interest` on the write side; every legitimate caller holds interest
          // before it reads (a listing via `sessionSkus`, a card via `retainSkus`,
          // `refetchTracked` by construction).
          const interested = trackedUnion();
          // ...and the claim generation as it stood when we ASKED, so a SKU that was released
          // and re-claimed while this was on the wire is not written from a read that belongs
          // to the interest before the gap (see `claimGenerations`). Renewed interest alone
          // cannot tell those apart — from `interested`'s point of view the SKU never left.
          const claimed = (sku: string): boolean =>
            (claimGenerations[sku] ?? 0) === batch.claimGens.get(sku);
          for (const state of states) {
            if (!interested.has(state.skuCode) || !claimed(state.skuCode)) continue;
            if ((writeStamps[state.skuCode] ?? 0) > startStamp) continue; // newer write wins
            writeStamps[state.skuCode] = startStamp;
            next[state.skuCode] = {
              band: state.band,
              status: state.status,
              quantityOnHand: state.quantityOnHand,
            };
          }
          // A requested SKU the server no longer knows: drop any stale entry (badge
          // disappears rather than lying), unless something newer wrote it meanwhile.
          // Only SKUs the read genuinely ANSWERED are eligible: a SKU whose chunk failed
          // is absent from `states` for a completely different reason, and tombstoning it
          // would record an outage as "no stock row at this depot" — the empty-vs-failed
          // conflation, one write further down than the two signals that guard against it.
          for (const sku of toFetch) {
            if (returned.has(sku) || !interested.has(sku) || unread.has(sku)) continue;
            if (!claimed(sku)) continue; // released and re-claimed since we asked
            if ((writeStamps[sku] ?? 0) > startStamp) continue; // newer write wins
            // TOMBSTONE the omission at this read's stamp instead of deleting the stamp with
            // the entry. "No row at this depot" is a RESULT, and it has to outrank an older
            // read the same way a returned row does: deleting the stamp reset the comparison
            // to `0 > startStamp`, so a slower PRE-GAP response — which `refetchTracked`
            // deliberately makes concurrent — passed the guard and wrote the stale row back
            // (round 11, F3). Stamped even when there was no entry to drop, so a read that
            // arrives out of order can never fill a hole a newer read established as empty.
            writeStamps[sku] = startStamp;
            delete next[sku];
          }
          set({ entries: next, checkedSkus: checkedProjection() });
          // A SKU both loops skipped on `!claimed` was answered by the server and then
          // deliberately not written, because the claim it was read for has since been retired
          // and re-taken. That is SUPERSEDED for the same reason a depot switch is: a request was
          // spent, and its result cannot be used for the claim that is live now. Saying so is
          // what stops it being reported as `'answered'` — which is a lie about the store, since
          // the SKU has no entry and no `checked` mark, and it left the read cycle believing the
          // question was settled: `established` went true with nothing established, so the card
          // showed neither substitutes nor an error until a push happened to answer them. React's
          // StrictMode setup → cleanup → setup makes exactly this sequence on every mount in dev,
          // and any remount or release-and-reclaim inside the flight window makes it in prod.
          // Excludes SKUs already `unread`: those failed at the endpoint, which is a stronger and
          // separately-charged fact (see `loadForSkus`'s precedence).
          superseded = new Set(toFetch.filter((sku) => !claimed(sku) && !unread.has(sku)));
        } else {
          // The switch landed WHILE the fetch was in flight. A request was spent, but its
          // result belongs to a depot we have left and is discarded above — so this is
          // SUPERSEDED for the same reason as the pre-flush case: nothing about the endpoint
          // was learned, and the abandoned cycle must not be laddered or parked on an error
          // for it (ADO-1528 AC6).
          superseded = new Set(toFetch);
          unread = new Set<string>();
        }
      }
    } catch {
      // `fetchAvailability` reports a failed chunk rather than throwing, so reaching here
      // means something unexpected did (a bad payload shape). Retain last good entries —
      // a failed availability read never blanks badges that were already on screen
      // (advisory data, AC1) — and leave every SKU unread, so the OOS-gate shows an error
      // rather than mistaking an uncheckable read for "none in stock here". A request WAS
      // spent to get here, so this is `unread` and not `superseded`.
      unread = new Set(toFetch);
      superseded = new Set<string>();
    } finally {
      for (const sku of toFetch) {
        if (inFlightReads[inFlightKey(depotId, sku)] === batch.promise) {
          delete inFlightReads[inFlightKey(depotId, sku)];
        }
      }
      batch.settle({ unread, superseded });
    }
  }

  /** Add `skus` to the tick's open batch (opening one if needed) and return its promise. */
  function enqueueRead(depotId: string, skus: readonly string[]): Promise<BatchOutcome> {
    if (pendingBatch === null || pendingBatch.depotId !== depotId) {
      let settle!: (outcome: BatchOutcome) => void;
      const promise = new Promise<BatchOutcome>((resolve) => {
        settle = resolve;
      });
      const batch: PendingBatch = {
        depotId,
        skus: new Set(),
        promise,
        settle,
        startStamp: ++writeStampCounter,
        claimGens: new Map(),
      };
      pendingBatch = batch;
      queueMicrotask(() => void flushBatch(batch));
    }
    const batch = pendingBatch;
    for (const sku of skus) {
      batch.skus.add(sku);
      // Overwrite deliberately: reaching here means the SKU had no in-flight read, so if a
      // release evicted it earlier in this same tick, the generation recorded now is the LIVE
      // claim's — which is the one this request is being sent on behalf of.
      batch.claimGens.set(sku, claimGenerations[sku] ?? 0);
      inFlightReads[inFlightKey(depotId, sku)] = batch.promise;
    }
    return batch.promise;
  }

  return {
    depotId: null,
    entries: {},
    checkedSkus: new Set<string>(),
    trackedSkus: new Set<string>(),

    async loadForSkus(depotId, skuCodes, options) {
      const track = options?.track ?? true;
      const unique = [...new Set(skuCodes)].filter((sku) => sku !== '');
      if (unique.length === 0) return 'answered'; // nothing to fetch — trivially settled

      // A depot switch invalidates everything held for the previous depot — including
      // any in-flight reads and the open batch, whose results belong to the old depot.
      // Per-card retains survive it (they track which cards are mounted, not stock).
      if (get().depotId !== null && get().depotId !== depotId) {
        writeStamps = {};
        inFlightReads = {};
        pendingBatch = null;
        // A switch resets a listing's session interest to what it is now asking for — but
        // only a TRACKING read carries session interest at all. A `track: false` read must
        // leave it alone rather than replace it with nothing: the per-card candidate read
        // is normally the FIRST caller at the new depot (a child effect runs before its
        // parent listing's), so clearing it here dropped every mounted listing's SKUs and
        // registered none in their place. It survived only because each `useAvailability`
        // consumer happened to re-assert `track: true` in the same flush — nothing enforced
        // that ordering. Session interest is "which listings are on screen", which a depot
        // switch does not change; the depot-scoped state is the entries.
        if (track) sessionSkus = new Set(unique);
        set({
          depotId,
          entries: {},
          checkedSkus: checkedProjection(),
          trackedSkus: trackedUnion(),
        });
      } else {
        if (track) for (const sku of unique) sessionSkus.add(sku);
        set({ depotId, trackedSkus: trackedUnion() });
      }

      // Coalesce: a SKU already on the wire at this depot RIDES that read; the rest join
      // this tick's batch. We await BOTH so the caller's "settled" signal fires once
      // every SKU it asked for has landed — not just the ones it put on the wire.
      const riding = new Set<Promise<BatchOutcome>>();
      const toFetch: string[] = [];
      for (const sku of unique) {
        const existing = inFlightReads[inFlightKey(depotId, sku)];
        if (existing !== undefined) riding.add(existing);
        else toFetch.push(sku);
      }
      if (toFetch.length > 0) riding.add(enqueueRead(depotId, toFetch));

      // THIS caller's outcome is derived over the SKUs IT asked for. The reads it awaits are
      // shared — merged across the tick, chunked under that, and ridden by later callers — so
      // their sets cover SKUs this caller never asked about. Reporting a shared failure to
      // every participant is what made one chunk's 500 fail an entire commit's worth of cards.
      const outcomes = await Promise.all([...riding]);
      if (unique.some((sku) => outcomes.some((o) => o.unread.has(sku)))) return 'unread';
      if (unique.some((sku) => outcomes.some((o) => o.superseded.has(sku)))) return 'superseded';
      return 'answered';
    },

    retainSkus(skuCodes) {
      let changed = false;
      for (const sku of new Set(skuCodes)) {
        if (sku === '') continue;
        const count = retainedRefs[sku] ?? 0;
        retainedRefs[sku] = count + 1;
        if (count === 0) changed = true;
      }
      if (changed) set({ trackedSkus: trackedUnion() });
    },

    releaseSkus(skuCodes) {
      let changed = false;
      const evicted: string[] = [];
      for (const sku of new Set(skuCodes)) {
        const count = retainedRefs[sku];
        // Tolerate a release with no matching claim (a reset() between retain and
        // cleanup): never let the count go negative and never throw on the way out.
        if (count === undefined) continue;
        if (count <= 1) {
          delete retainedRefs[sku];
          changed = true;
          // Last claim gone. A listing's session interest outlives any card, so only a SKU
          // with none of that leaves the tracked set — and an untracked entry is
          // unobservable, so it goes with it (see the `releaseSkus` contract above).
          if (!sessionSkus.has(sku)) evicted.push(sku);
        } else {
          retainedRefs[sku] = count - 1;
        }
      }
      if (evicted.length > 0) {
        const depot = get().depotId;
        const next = { ...get().entries };
        for (const sku of evicted) {
          delete next[sku];
          delete writeStamps[sku];
          // Drop the in-flight entry too, so the next claim's read genuinely goes on the
          // wire instead of riding a request issued BEFORE the untracked gap — the same
          // reason `refetchTracked` drops them. The abandoned read still settles, but
          // `flushBatch` no longer writes a SKU nothing is interested in.
          if (depot !== null) delete inFlightReads[inFlightKey(depot, sku)];
          // Retire the CLAIM this eviction ends, so the abandoned read is disqualified by
          // identity rather than by nobody happening to claim the SKU again in time. Without
          // this, a claim returning before that read settled saw its stale row written back —
          // renewed interest and a deleted stamp both let it through (round 15, F2).
          claimGenerations[sku] = (claimGenerations[sku] ?? 0) + 1;
        }
        // The eviction takes the SKU's `checked` mark with it (`writeStamps` was just
        // cleared for it): nothing tracks it, so the answer we held can go arbitrarily
        // stale, and a later claim must re-read rather than treat it as established.
        set({ entries: next, checkedSkus: checkedProjection(), trackedSkus: trackedUnion() });
      } else if (changed) {
        set({ trackedSkus: trackedUnion() });
      }
    },

    applyPush(push) {
      const { depotId, entries, trackedSkus } = get();
      // Pushes for another depot's room can't happen by construction (the socket
      // joins one room), but guard anyway — never patch across depots.
      if (depotId !== null && push.depotId !== depotId) return;
      const stamp = ++writeStampCounter;
      const next: Record<string, AvailabilityEntry> = { ...entries };
      let changed = false;
      for (const entry of push.entries) {
        // Wire payloads are unvalidated JSON — a band/status this build doesn't
        // know (rolling-deploy skew) must never reach a render, where an unknown
        // band would crash every card for that SKU (7.3 review finding).
        if (!KNOWN_BANDS.has(entry.band) || !KNOWN_STATUSES.has(entry.status)) continue;
        // Likewise a non-finite quantity (NaN/±Infinity from a malformed or partial
        // push) must never be stored: it renders as "NaN in store" on the Stock Card
        // badge AND "NaN currently in stock" on the PDP Availability row, since both
        // read this one entry (PR #282 review). Skip the whole entry, matching the
        // band/status posture — an unusable quantity is no answer at all.
        if (!Number.isFinite(entry.quantityOnHand)) continue;
        // Only SKUs a listing tracks — a full-feed push would otherwise grow the
        // store unboundedly with entries nothing can render (7.3 review finding).
        if (!trackedSkus.has(entry.skuCode)) continue;
        writeStamps[entry.skuCode] = stamp;
        next[entry.skuCode] = {
          band: entry.band,
          status: entry.status,
          quantityOnHand: entry.quantityOnHand,
        };
        changed = true;
      }
      // A retired level is "no stock data": drop the entry so the card renders no badge, exactly
      // as a read that returns no row tombstones it, stamped so an older read cannot fill it back.
      for (const sku of push.noStockDataSkuCodes ?? []) {
        if (typeof sku !== 'string' || !trackedSkus.has(sku)) continue;
        writeStamps[sku] = stamp;
        delete next[sku];
        changed = true;
      }
      // A push is an ANSWER for the SKUs it carries, so it establishes checked-ness the
      // same way a read does — that is what lets a card parked on a failed candidate read
      // self-heal when stock next moves.
      if (changed) set({ entries: next, checkedSkus: checkedProjection() });
    },

    async refetchTracked() {
      const { depotId, trackedSkus } = get();
      if (depotId === null || trackedSkus.size === 0) return;
      // The reconnect catch-up must GENUINELY re-read every tracked SKU — it must not ride
      // an in-flight read that was requested BEFORE the socket gap closed, because that read
      // returns pre-gap data and refreshes nothing for those SKUs, defeating the catch-up's
      // "cheap consistency after a gap" contract. Drop the tracked SKUs'
      // in-flight entries so this read goes on the wire fresh; a concurrent NEW caller then
      // coalesces onto THIS read instead. The pre-gap fetch still completes, but its write
      // carries an older stamp, so the catch-up's newer result wins per-SKU — INCLUDING the
      // SKUs the catch-up found no row for, which it tombstones at its own stamp rather than
      // leaving unstamped for the pre-gap row to fill (see flushBatch). (Merging into
      // an OPEN same-tick batch is safe and deliberate: nothing has gone on the wire yet,
      // so the shared request is sent — and answered — after the gap closed.)
      for (const sku of trackedSkus) delete inFlightReads[inFlightKey(depotId, sku)];
      // `track: false` — every SKU here is tracked by construction; re-adding them would
      // promote per-card retained claims into permanent session interest, defeating the
      // release-on-unmount that keeps this set bounded.
      await get().loadForSkus(depotId, [...trackedSkus], { track: false });
    },

    reset() {
      writeStamps = {};
      inFlightReads = {};
      pendingBatch = null;
      sessionSkus = new Set<string>();
      retainedRefs = {};
      claimGenerations = {};
      set({
        depotId: null,
        entries: {},
        checkedSkus: new Set<string>(),
        trackedSkus: new Set<string>(),
      });
    },
  };
});
